/**
 * One-shot automatic reload after a crash.
 *
 * A good share of crashes in a deployed SPA are recoverable by reloading: the usual one
 * is a tab left open across a deploy, whose bundle asks for a lazy chunk that no longer
 * exists. Those users can recover on their own.
 *
 * But reloading unconditionally is worse than not reloading at all. When the cause
 * survives a reload — browser translation rewriting the DOM, a deterministic bug in a
 * view — the page crashes again on load and reloads again, forever. The user cannot
 * read the error, cannot click anything, and each cycle re-downloads a ~5 MB bundle and
 * replays the startup queries against the API.
 *
 * So: one attempt, then give up and show the error screen.
 */

const STORAGE_KEY = 'c1do1:crash-auto-reload';

// A crash half an hour into the session is a different incident from the one at load,
// and deserves its own attempt. Only reloads close together are treated as a loop.
const ATTEMPT_TTL_MS = 30 * 1000;

const now = () => Date.now();

const readStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    // Safari in private mode, and any embedding that blocks storage.
    return null;
  }
};

/**
 * Claims the single reload attempt for this crash, if one is available.
 * Session-scoped on purpose: the flag must not outlive the tab.
 *
 * @returns {boolean} true if the caller should reload; false if it already tried
 */
export const consumeAutoReloadAttempt = () => {
  const storage = readStorage();
  if (!storage) return false;

  try {
    const last = Number(storage.getItem(STORAGE_KEY)) || 0;
    if (last && now() - last < ATTEMPT_TTL_MS) return false;

    storage.setItem(STORAGE_KEY, String(now()));
    return true;
  } catch {
    // Storage present but unwritable (quota, hardened settings): never reload blindly.
    return false;
  }
};

/** Test helper. */
export const clearAutoReloadAttempt = () => {
  const storage = readStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
};
